import { queryOptions } from "@tanstack/react-query";

import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";
import {
  type InboundCallsSnapshotParams,
  type InboundCallsSnapshotResponse,
  isInboundCallsSnapshotResponse,
} from "./inboundCallsTypes";

/** Domain-local query keys/fetchers for Inbound Calls. */
export const inboundCallsQueryKeys = {
  all: [...operatorsQueryKeys.all, "dashboards", "inbound-calls"] as const,
  snapshot: (params: InboundCallsSnapshotParams) =>
    [...inboundCallsQueryKeys.all, "snapshot", params] as const,
};

function throwIfUnauthorized(response: Response): void {
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
}

async function throwIfForbidden(response: Response, fallbackMessage: string): Promise<void> {
  if (response.status !== 403) {
    return;
  }

  const message = await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage);
  throw new OperatorsForbiddenError(message || fallbackMessage);
}

export async function fetchInboundCallsSnapshot(
  params: InboundCallsSnapshotParams,
): Promise<InboundCallsSnapshotResponse> {
  const search = new URLSearchParams({
    start: params.start,
    end: params.end,
    scope: params.scope,
  });
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/inbound-calls/snapshot?${search}`,
    { credentials: "include" },
  );
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load Inbound Calls snapshot",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isInboundCallsSnapshotResponse(body)) {
    throw new Error("Inbound Calls snapshot response was malformed");
  }
  return body;
}

export function inboundCallsSnapshotQueryOptions(params: InboundCallsSnapshotParams) {
  return queryOptions({
    queryKey: inboundCallsQueryKeys.snapshot(params),
    queryFn: () => fetchInboundCallsSnapshot(params),
  });
}
